import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { useCart } from '../contexts/CartContext';
import CartSidebar from '../components/CartSidebar';

const { FiSearch, FiStar, FiClock, FiLayers, FiHeart, FiArrowRight, FiZap, FiShoppingCart } = FiIcons;

const PublicCatalog = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { bookmarks, toggleBookmark, cart } = useCart();

  useEffect(() => {
    const fetchAssessments = async () => {
      const { data, error } = await supabase
        .from('assessments_1773012000000')
        .select('*')
        .eq('is_active', true);
      if (!error) setAssessments(data);
      setLoading(false);
    };
    fetchAssessments();
  }, []);

  const filtered = assessments.filter(a => 
    (filter === 'all' || a.category === filter) && 
    (a.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const categories = [
    'all', 'strategy', 'operations', 'marketing', 'finance', 'sales', 'hr', 'technology'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Business Assessments Catalog | Cansulta Business 360</title>
        <meta name="description" content="Browse our comprehensive library of AI-powered and expert-led business assessments." />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-slate-900 text-white py-20 px-4">
        <div className="max-w-7xl auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-black mb-6 font-ubuntu"
          >
            Measure What <span className="text-blue-500">Matters.</span>
          </motion.h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            Professional business audits designed by industry experts, powered by AI. Get instant clarity on your operational health.
          </p>
          <div className="max-w-2xl mx-auto relative group">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Search assessments (e.g., Marketing, Strategy...)" 
              className="w-full pl-12 pr-4 py-5 bg-white/10 border border-white/10 rounded-2xl focus:bg-white focus:text-gray-900 focus:ring-4 focus:ring-blue-500/20 transition-all text-lg backdrop-blur-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters */}
          <aside className="lg:w-64 space-y-8">
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Categories</h3>
              <div className="space-y-1">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setFilter(cat)}
                    className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-bold transition-all capitalize ${filter === cat ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {cat.replace('-', ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-xl">
              <SafeIcon icon={FiZap} className="w-8 h-8 mb-4 text-blue-200" />
              <h4 className="font-bold mb-2">Unlimited Access</h4>
              <p className="text-xs text-blue-100 mb-4 leading-relaxed">Get every assessment in our catalog for one low monthly price.</p>
              <Link to="/pricing" className="block w-full py-2 bg-white text-blue-600 rounded-lg text-center text-xs font-bold hover:bg-blue-50 transition-colors">
                View Plans
              </Link>
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-1 grid md:grid-cols-2 gap-8">
            {loading ? (
              <div className="col-span-2 py-20 text-center text-gray-400 italic">Initialising catalog...</div>
            ) : filtered.length === 0 ? (
              <div className="col-span-2 py-20 text-center text-gray-400 italic">No assessments found in this category.</div>
            ) : (
              filtered.map((a, i) => (
                <motion.div 
                  key={a.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-2xl hover:border-blue-200 transition-all group"
                >
                  <div className="p-8">
                    <div className="flex justify-between items-start mb-6">
                      <div className={`p-3 rounded-2xl ${a.complexity === 'advanced' ? 'bg-red-50 text-red-600' : a.complexity === 'intermediate' ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'}`}>
                        <SafeIcon icon={FiLayers} className="w-6 h-6" />
                      </div>
                      <button 
                        onClick={() => toggleBookmark(a.id)}
                        className={`p-2 rounded-full transition-colors ${bookmarks.includes(a.id) ? 'bg-red-50 text-red-500' : 'text-gray-300 hover:text-red-500'}`}
                      >
                        <SafeIcon icon={FiHeart} className={bookmarks.includes(a.id) ? 'fill-current' : ''} />
                      </button>
                    </div>
                    <Link to={`/assessment/${a.slug}`} className="block group-hover:text-blue-600 transition-colors">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 font-ubuntu">{a.title}</h3>
                    </Link>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6 line-clamp-2">{a.description}</p>
                    <div className="flex items-center gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-8">
                      <span className="flex items-center gap-1"><SafeIcon icon={FiClock} /> {a.duration}</span>
                      <span className="flex items-center gap-1"><SafeIcon icon={FiStar} /> {a.rating} Rating</span>
                    </div>
                    <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Starts at</p>
                        <p className="text-2xl font-black text-gray-900">${a.price_ai}</p>
                      </div>
                      <Link 
                        to={`/assessment/${a.slug}`}
                        className="bg-gray-900 text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-blue-600 transition-all flex items-center gap-2"
                      >
                        View Details <SafeIcon icon={FiArrowRight} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Cart Summary Floating Button */}
      {cart.length > 0 && (
        <button 
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-8 right-8 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-2xl shadow-blue-300 flex items-center gap-3 hover:scale-105 active:scale-95 transition-all z-50 font-bold"
        >
          <SafeIcon icon={FiShoppingCart} />
          <span>Cart • {cart.length}</span>
          <span className="bg-white/20 px-2 py-0.5 rounded text-xs">${cart.reduce((s, i) => s + i.price, 0)}</span>
        </button>
      )}

      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default PublicCatalog;